import {
  createPanelFrame,
  resolveTitle,
  createBadge,
  createMetric,
  formatPercent,
  formatNumber,
  toNumber,
  clamp
} from './panel-utils.js';

const DEFAULT_FEATURES = [
  { name: 'user_country', psi: 0.04, ks: 0.03 },
  { name: 'device_type', psi: 0.12, ks: 0.08 },
  { name: 'session_length', psi: 0.26, ks: 0.19 },
  { name: 'purchase_value', psi: 0.31, ks: 0.27 },
  { name: 'referrer_domain', psi: 0.18, ks: 0.11 },
  { name: 'cart_depth', psi: 0.22, ks: 0.16 }
];

export function render({ node, api }) {
  const { root, body, titleEl, metaEl } = createPanelFrame(node, 'Feature Drift Monitor');
  const metrics = document.createElement('div');
  metrics.className = 'ew-metric-grid';

  const list = document.createElement('div');
  list.className = 'ew-list';

  body.append(metrics, list);

  const update = (next) => {
    titleEl.textContent = resolveTitle(next, 'Feature Drift Monitor');
    const threshold = toNumber(next?.props?.threshold ?? next?.data?.threshold, 0.2);
    const windowLabel = next?.props?.window ?? next?.data?.window ?? '24h';
    const baseline = next?.props?.baseline ?? next?.data?.baseline ?? '';
    metaEl.textContent = baseline ? `baseline ${baseline}` : '';

    const raw = next?.data?.features ?? next?.props?.features;
    const features = Array.isArray(raw) && raw.length ? raw : DEFAULT_FEATURES;
    const scored = features.map((f) => ({
      ...f,
      score: toNumber(f.drift ?? f.psi ?? f.ks, 0)
    }));
    scored.sort((a, b) => b.score - a.score);

    const total = scored.length;
    const drifted = scored.filter((f) => f.score >= threshold).length;
    const maxScore = Math.max(1, threshold, ...scored.map((f) => f.score));

    metrics.innerHTML = '';
    metrics.append(
      createMetric('drifted', `${drifted}/${total}`),
      createMetric('window', windowLabel),
      createMetric('threshold', formatPercent(threshold, 0))
    );

    list.innerHTML = '';
    const selected = next?.data?.selected;
    scored.slice(0, 8).forEach((feature) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'ew-list-item';
      if (selected && selected === feature.name) item.classList.add('is-active');

      const head = document.createElement('div');
      head.className = 'ew-list-head';
      const name = document.createElement('span');
      name.className = 'ew-list-title';
      name.textContent = feature.name;
      const tone = feature.score >= threshold ? 'alert' : feature.score >= threshold * 0.6 ? 'warn' : 'ok';
      const badge = createBadge(tone === 'alert' ? 'drift' : 'stable', tone);
      head.append(name, badge);

      const meta = document.createElement('div');
      meta.className = 'ew-list-meta';
      const psi = formatNumber(toNumber(feature.psi, 0), 2);
      const ks = formatNumber(toNumber(feature.ks, 0), 2);
      meta.textContent = `psi ${psi} � ks ${ks}`;

      const progress = document.createElement('div');
      progress.className = 'ew-progress';
      const bar = document.createElement('span');
      bar.className = `ew-progress-bar ew-progress-${tone}`;
      const width = clamp(feature.score / maxScore, 0, 1) * 100;
      bar.style.width = `${width}%`;
      progress.appendChild(bar);

      item.append(head, meta, progress);
      item.addEventListener('click', () => {
        api.setData({ selected: feature.name });
        api.emit('node:input', { id: node.id, field: 'feature', value: feature.name });
      });
      list.appendChild(item);
    });
  };

  update(node);
  return { el: root, update };
}
